import React, { useState, useEffect } from 'react';
import { Spin, Empty, Progress, Tag } from 'antd';
import {
  FolderKanban, Users, Zap, Ticket, CheckCircle,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { PROJECTS_DATA } from '../../constants/api';
import ExportButton from '../../components/common/ExportButton';
import { MetricCard } from '../../components/insights';

const ProjectsOverview = () => {
  const { fetch } = useApiMethods();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const res = await fetch(PROJECTS_DATA, {}, setLoading);
      if (res?.status !== 'fail') {
        const d = res?.data || res;
        setProjects(Array.isArray(d) ? d : []);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex items-center justify-center h-screen"><Spin size="large" /></div>;

  const totalTickets = projects.reduce((s, p) => s + (p.total_tickets || 0), 0);
  const totalOpen = projects.reduce((s, p) => s + (p.open_tickets || 0), 0);
  const totalDone = projects.reduce((s, p) => s + (p.done_tickets || 0), 0);
  const totalSprints = projects.reduce((s, p) => s + (p.active_sprints || 0), 0);

  return (
    <div className="p-6 space-y-6 bg-background min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Projects</h2>
          <p className="text-sm text-muted-foreground">Active projects from FixDesk</p>
        </div>
        <ExportButton
          data={projects}
          filename="projects"
          pdfColumns={[
            { title: 'Project', dataKey: 'name' },
            { title: 'Manager', dataKey: 'manager' },
            { title: 'Tickets', dataKey: 'total_tickets' },
            { title: 'Open', dataKey: 'open_tickets' },
            { title: 'Done', dataKey: 'done_tickets' },
            { title: '% Done', dataKey: 'completion_pct' },
            { title: 'Sprints', dataKey: 'active_sprints' },
            { title: 'Team', dataKey: 'team_size' },
          ]}
          pdfTitle="Projects Report"
        />
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={FolderKanban} intent="primary" title="Active Projects" value={projects.length} />
        <MetricCard icon={Ticket} intent="primary" title="Total Tickets" value={totalTickets} subtitle={`${totalOpen} open · ${totalDone} done`} />
        <MetricCard icon={Zap} intent="warning" title="Active Sprints" value={totalSprints} />
        <MetricCard icon={CheckCircle} intent="positive" title="Overall Completion" value={`${totalTickets > 0 ? Math.round(totalDone / totalTickets * 100) : 0}%`} />
      </div>

      {/* Project Cards */}
      {projects.length === 0 ? (
        <Empty description="No projects found" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {projects.map((proj) => {
            const pct = proj.completion_pct || 0;
            const color = pct >= 70 ? '#10b981' : pct >= 40 ? '#6366f1' : '#f59e0b';
            return (
              <div key={proj.id} className="rounded-2xl bg-card border border-border shadow-sm p-5 hover:shadow-md transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-foreground text-lg">{proj.name}</h3>
                    {proj.manager && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <Users className="w-3 h-3" /> {proj.manager}
                      </div>
                    )}
                  </div>
                  <div className="text-2xl font-bold" style={{ color }}>{pct}%</div>
                </div>

                <Progress percent={pct} showInfo={false} strokeColor={color} strokeWidth={6} className="mb-4" />

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-2 rounded-xl bg-muted">
                    <div className="text-lg font-bold text-foreground">{proj.total_tickets}</div>
                    <div className="text-[10px] text-muted-foreground">Tickets</div>
                  </div>
                  <div className="text-center p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/30">
                    <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{proj.done_tickets}</div>
                    <div className="text-[10px] text-muted-foreground">Done</div>
                  </div>
                  <div className="text-center p-2 rounded-xl bg-amber-50 dark:bg-amber-950/30">
                    <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{proj.open_tickets}</div>
                    <div className="text-[10px] text-muted-foreground">Open</div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
                  <div className="flex gap-3">
                    <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {proj.active_sprints}/{proj.total_sprints} sprints</span>
                    <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {proj.team_size} members</span>
                  </div>
                  {proj.client_count > 0 && <Tag className="text-[10px]">{proj.client_count} clients</Tag>}
                </div>

                {proj.description && <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{proj.description}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectsOverview;
